import React, { useCallback, useEffect, useRef, useState } from 'react';

import PlayArrowIcon from '@mui/icons-material/PlayArrow';
import StopIcon from '@mui/icons-material/Stop';
import { Box, IconButton, Stack, Typography } from '@mui/material';

function formatTime(seconds: number): string {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${String(mins).padStart(2, '0')}:${String(secs).padStart(2, '0')}`;
}

const BAR_COUNT = 200;
const TRACK_HEIGHT = 56;
const HANDLE_WIDTH = 12;

interface AudioTimelineProps {
  audioUrl: string;
  audioDuration: number;
  clipDuration: number;
  startTime: number;
  onStartTimeChange: (startTime: number) => void;
}

export default function AudioTimeline({
  audioUrl,
  audioDuration,
  clipDuration,
  startTime,
  onStartTimeChange,
}: AudioTimelineProps) {
  const trackRef = useRef<HTMLDivElement>(null);
  const isDragging = useRef(false);
  const dragOffsetX = useRef(0);
  const [localStart, setLocalStart] = useState(startTime);
  const [waveform, setWaveform] = useState<number[]>([]);
  const [loadingWaveform, setLoadingWaveform] = useState(true);
  const [isPlaying, setIsPlaying] = useState(false);
  const audioRef = useRef<HTMLAudioElement | null>(null);

  const stopPlayback = useCallback(() => {
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current.removeAttribute('src');
      audioRef.current = null;
    }
    setIsPlaying(false);
  }, []);

  useEffect(() => {
    setLocalStart(startTime);
  }, [startTime]);

  useEffect(() => {
    if (!audioUrl) return;

    let cancelled = false;
    setLoadingWaveform(true);
    setWaveform([]);

    (async () => {
      try {
        const response = await fetch(audioUrl);
        const arrayBuffer = await response.arrayBuffer();
        const audioCtx = new AudioContext();
        const audioBuffer = await audioCtx.decodeAudioData(arrayBuffer);
        audioCtx.close();

        if (cancelled) return;

        const channelData = audioBuffer.getChannelData(0);
        const samplesPerBar = Math.floor(channelData.length / BAR_COUNT);
        const bars: number[] = [];

        for (let i = 0; i < BAR_COUNT; i++) {
          let sum = 0;
          const offset = i * samplesPerBar;
          for (let j = 0; j < samplesPerBar; j++) {
            sum += Math.abs(channelData[offset + j]);
          }
          bars.push(sum / samplesPerBar);
        }

        const maxAmplitude = Math.max(...bars, 0.001);
        const normalized = bars.map((b) => b / maxAmplitude);

        if (!cancelled) {
          setWaveform(normalized);
          setLoadingWaveform(false);
        }
      } catch {
        if (!cancelled) {
          setLoadingWaveform(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [audioUrl]);

  useEffect(() => {
    stopPlayback();
  }, [localStart, stopPlayback]);

  useEffect(() => {
    return () => {
      audioRef.current?.pause();
    };
  }, []);

  const handleTogglePlayback = useCallback(() => {
    if (isPlaying) {
      stopPlayback();
      return;
    }

    const audio = new Audio(audioUrl);
    audioRef.current = audio;
    audio.currentTime = localStart;

    const clipEnd = localStart + clipDuration;

    const onTimeUpdate = () => {
      if (audio.currentTime >= clipEnd) {
        stopPlayback();
      }
    };

    const onEnded = () => stopPlayback();

    audio.addEventListener('timeupdate', onTimeUpdate);
    audio.addEventListener('ended', onEnded);
    audio.play();
    setIsPlaying(true);
  }, [isPlaying, audioUrl, localStart, clipDuration, stopPlayback]);

  const maxStart = Math.max(0, audioDuration - clipDuration);
  const windowFraction = audioDuration > 0 ? clipDuration / audioDuration : 1;

  const getStartFromClientX = useCallback(
    (clientX: number) => {
      if (!trackRef.current) return localStart;
      const rect = trackRef.current.getBoundingClientRect();
      const innerWidth = rect.width - HANDLE_WIDTH * 2;
      const relX = clientX - rect.left - HANDLE_WIDTH - dragOffsetX.current;
      const fraction = relX / innerWidth;
      const newStart = fraction * audioDuration;
      return Math.max(0, Math.min(maxStart, newStart));
    },
    [audioDuration, maxStart, localStart],
  );

  const handleMouseDown = useCallback(
    (e: React.MouseEvent) => {
      if (!trackRef.current) return;
      isDragging.current = true;
      const rect = trackRef.current.getBoundingClientRect();
      const innerWidth = rect.width - HANDLE_WIDTH * 2;
      const windowLeft = (localStart / audioDuration) * innerWidth;
      const windowWidth = windowFraction * innerWidth;
      const clickX = e.clientX - rect.left - HANDLE_WIDTH;

      if (clickX >= windowLeft && clickX <= windowLeft + windowWidth) {
        dragOffsetX.current = clickX - windowLeft;
      } else {
        dragOffsetX.current = windowWidth / 2;
        const newStart = getStartFromClientX(e.clientX);
        setLocalStart(newStart);
      }
      e.preventDefault();
    },
    [localStart, audioDuration, windowFraction, getStartFromClientX],
  );

  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDragging.current) return;
      const newStart = getStartFromClientX(e.clientX);
      setLocalStart(newStart);
    },
    [getStartFromClientX],
  );

  const handleMouseUp = useCallback(() => {
    if (isDragging.current) {
      isDragging.current = false;
      onStartTimeChange(localStart);
    }
  }, [localStart, onStartTimeChange]);

  useEffect(() => {
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);
    };
  }, [handleMouseMove, handleMouseUp]);

  const endTime = Math.min(localStart + clipDuration, audioDuration);
  const windowLeftPercent = audioDuration > 0 ? (localStart / audioDuration) * 100 : 0;
  const windowWidthPercent = windowFraction * 100;

  return (
    <Box sx={{ width: '100%', userSelect: 'none' }}>
      <Stack direction="row" justifyContent="space-between" sx={{ mb: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          Start: {formatTime(localStart)}
        </Typography>
        <Typography variant="caption" sx={{ fontWeight: 600 }}>
          Clip: {formatTime(clipDuration)}
        </Typography>
        <Typography variant="caption" color="text.secondary">
          End: {formatTime(endTime)}
        </Typography>
      </Stack>

      <Box
        ref={trackRef}
        onMouseDown={handleMouseDown}
        sx={{
          position: 'relative',
          height: TRACK_HEIGHT,
          borderRadius: 1,
          overflow: 'hidden',
          cursor: 'pointer',
          bgcolor: 'grey.900',
        }}
      >
        {waveform.length > 0 && (
          <Stack direction="row" alignItems="center" sx={{ position: 'absolute', inset: 0, px: '2px', gap: '1px' }}>
            {waveform.map((amplitude, i) => (
              <Box
                key={i}
                sx={{
                  flex: '1 0 0',
                  height: `${Math.max(4, amplitude * 100)}%`,
                  bgcolor: 'primary.main',
                  borderRadius: 0.5,
                  opacity: 0.7,
                  minWidth: 0,
                }}
              />
            ))}
          </Stack>
        )}

        {loadingWaveform && waveform.length === 0 && (
          <Stack alignItems="center" justifyContent="center" sx={{ position: 'absolute', inset: 0 }}>
            <Typography variant="caption" color="grey.400">
              Loading waveform...
            </Typography>
          </Stack>
        )}

        <Box
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: 0,
            width: `${windowLeftPercent}%`,
            bgcolor: 'rgba(0,0,0,0.6)',
            pointerEvents: 'none',
          }}
        />
        <Box
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            right: 0,
            left: `${windowLeftPercent + windowWidthPercent}%`,
            bgcolor: 'rgba(0,0,0,0.6)',
            pointerEvents: 'none',
          }}
        />

        <Box
          sx={{
            position: 'absolute',
            top: 0,
            bottom: 0,
            left: `${windowLeftPercent}%`,
            width: `${windowWidthPercent}%`,
            pointerEvents: 'none',
            borderTop: '3px solid',
            borderBottom: '3px solid',
            borderColor: 'warning.main',
          }}
        >
          <Box
            sx={{
              position: 'absolute',
              left: -1,
              top: 0,
              bottom: 0,
              width: HANDLE_WIDTH,
              bgcolor: 'warning.main',
              borderRadius: '4px 0 0 4px',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box sx={{ width: 2, height: 16, bgcolor: 'warning.dark', borderRadius: 1 }} />
          </Box>
          <Box
            sx={{
              position: 'absolute',
              right: -1,
              top: 0,
              bottom: 0,
              width: HANDLE_WIDTH,
              bgcolor: 'warning.main',
              borderRadius: '0 4px 4px 0',
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'center',
            }}
          >
            <Box sx={{ width: 2, height: 16, bgcolor: 'warning.dark', borderRadius: 1 }} />
          </Box>
        </Box>
      </Box>

      <Stack direction="row" justifyContent="space-between" alignItems="center" sx={{ mt: 0.5 }}>
        <Typography variant="caption" color="text.secondary">
          {formatTime(0)}
        </Typography>
        <IconButton size="small" onClick={handleTogglePlayback}>
          {isPlaying ? <StopIcon fontSize="small" /> : <PlayArrowIcon fontSize="small" />}
        </IconButton>
        <Typography variant="caption" color="text.secondary">
          {formatTime(audioDuration)}
        </Typography>
      </Stack>
    </Box>
  );
}
